import React from 'react';
import { Popover, message } from 'antd';
import { openTextVisitUnSubmittedDrawer, openTextVisitSubmittedDrawer } from 'utils/globalLayerControl';
import RefillTasksPopoverContent from '../RefillTasksPopoverContent';
import type { EventWithTimeOverlap } from 'types/calendar';

type RefillTaskItem = React.ComponentProps<typeof RefillTasksPopoverContent>['items'][number];

export function isTaskLikeEvent(evt: Pick<EventWithTimeOverlap, 'type' | 'summary'>) {
    if (evt.type === 'task') return true;
    const summary = (evt.summary || '').trim();
    return summary.startsWith('📃') || summary.startsWith('✏️');
}

type Props = {
    events: EventWithTimeOverlap[];
    onEventUpdate?: () => void;
    placement?: React.ComponentProps<typeof Popover>['placement'];
    pillClassName: string;
    getPopupContainer?: React.ComponentProps<typeof Popover>['getPopupContainer'];
    onBeforeOpen?: () => void;
    as?: 'button' | 'div';
    onEdit?: (item: RefillTaskItem) => void;
};

const TaskPills: React.FC<Props> = ({
    events,
    onEventUpdate,
    placement = 'right',
    pillClassName,
    getPopupContainer,
    onBeforeOpen,
    as = 'button',
    onEdit,
}) => {
    const [openTaskId, setOpenTaskId] = React.useState<string>();

    const PillTag = as;

    return (
        <>
            {events.map((evt) => {
                const summary = (evt.summary || '').trim();
                const isTextVisitPill = summary.startsWith('✏️');
                const pillLabel = summary.replace(/^📃\s*/u, '').replace(/^✏️\s*/u, '');

                if (isTextVisitPill) {
                    const tvItems = ((evt as any).textVisitItems || []) as RefillTaskItem[];
                    const tvCount =
                        tvItems.length || Number.parseInt((pillLabel.match(/\d+/) || [])[0] || '0', 10) || 0;
                    const tvTotalLabel = tvCount === 1 ? '1 text visit' : `${tvCount} text visits`;
                    const tvPopContent = (
                        <RefillTasksPopoverContent
                            totalLabel={tvTotalLabel}
                            onClose={() => setOpenTaskId(undefined)}
                            items={tvItems}
                            onUpdated={() => {
                                onEventUpdate?.();
                            }}
                            onEdit={onEdit}
                            onView={(item) => {
                                setOpenTaskId(undefined);
                                if (!item.textVisitUuid) {
                                    message.error('Unable to open details: missing text visit id.');
                                    return;
                                }
                                if (!item.providerTreatmentSubmitted) {
                                    openTextVisitUnSubmittedDrawer(item.textVisitUuid, () => onEventUpdate?.(), true);
                                } else {
                                    openTextVisitSubmittedDrawer(item.textVisitUuid);
                                }
                            }}
                        />
                    );

                    return (
                        <Popover
                            key={evt.id}
                            open={openTaskId === evt.id}
                            onOpenChange={(v) => setOpenTaskId(v ? evt.id : undefined)}
                            placement={placement}
                            title={null}
                            content={tvPopContent}
                            trigger="click"
                            getPopupContainer={getPopupContainer}
                        >
                            <PillTag
                                {...(as === 'button' ? { type: 'button' as const } : null)}
                                className={pillClassName}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onBeforeOpen?.();
                                    setOpenTaskId(evt.id);
                                }}
                            >
                                <span aria-hidden>✏️</span>
                                <span>{pillLabel}</span>
                            </PillTag>
                        </Popover>
                    );
                }

                const refillItems = ((evt as any).prescriptionRefillItems || []) as RefillTaskItem[];
                const count = refillItems.length || Number.parseInt((pillLabel.match(/\d+/) || [])[0] || '0', 10) || 0;
                const totalLabel = count === 1 ? '1 prescription refill task' : `${count} prescription refill tasks`;
                const popContent = (
                    <RefillTasksPopoverContent
                        totalLabel={totalLabel}
                        onClose={() => setOpenTaskId(undefined)}
                        items={refillItems}
                        onUpdated={() => {
                            onEventUpdate?.();
                        }}
                        onEdit={onEdit}
                        onView={(item) => {
                            setOpenTaskId(undefined);
                            if (!item.textVisitUuid) {
                                message.error('Unable to open details: missing text visit id.');
                                return;
                            }
                            if (!item.providerTreatmentSubmitted) {
                                openTextVisitUnSubmittedDrawer(item.textVisitUuid, () => onEventUpdate?.(), true);
                            } else {
                                openTextVisitSubmittedDrawer(item.textVisitUuid);
                            }
                        }}
                    />
                );

                return (
                    <Popover
                        key={evt.id}
                        open={openTaskId === evt.id}
                        onOpenChange={(v) => setOpenTaskId(v ? evt.id : undefined)}
                        placement={placement}
                        title={null}
                        content={popContent}
                        trigger="click"
                        getPopupContainer={getPopupContainer}
                    >
                        <PillTag
                            {...(as === 'button' ? { type: 'button' as const } : null)}
                            className={pillClassName}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onBeforeOpen?.();
                                setOpenTaskId(evt.id);
                            }}
                        >
                            <span aria-hidden>📃</span>
                            <span>{pillLabel}</span>
                        </PillTag>
                    </Popover>
                );
            })}
        </>
    );
};

export default TaskPills;
